import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, FormItem } from 'vnetwork-ui'
import { createReseller } from '../../services'
import { CreateResellerRequest } from '../../types'
import { createResellerSchema } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getResellerTierOptions } from '../../constants'

export interface ResellerCreateFormValues {
    customer_id: number | null
    reseller_name: string
    reseller_tier: string
}

const New = () => {
    const { t } = useTranslation('reseller')
    const navigate = useNavigate()
    const location = useLocation()
    const initialValues = location.state as
        | Partial<ResellerCreateFormValues>
        | undefined

    const { control, handleSubmit } = useForm<ResellerCreateFormValues>({
        defaultValues: {
            customer_id: initialValues?.customer_id || null,
            reseller_name: initialValues?.reseller_name || '',
            reseller_tier: '',
        },
        resolver: yupResolver(createResellerSchema) as any,
        reValidateMode: 'onChange',
    })

    const { mutateAsync: submit, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: CreateResellerRequest) => createReseller(data),
        },
        {
            successMessage: t('reseller_created_successfully'),
            reactQueryKeys: QUERY_KEYS.RESELLERS,
            onSuccess: () => {
                navigate(paths.resellersList)
            },
        }
    )

    const onSubmit = async (data: ResellerCreateFormValues) => {
        if (!data.customer_id) return

        await submit({
            customer_id: data.customer_id,
            reseller_name: data.reseller_name,
            reseller_tier: data.reseller_tier || undefined,
        })
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.resellersList,
                        title: t('reseller_manager'),
                    },
                    {
                        path: paths.resellerCreate,
                        title: t('create_reseller'),
                    },
                ]}
            />

            <Paper className="mt-4">
                <h2 className="text-lg font-semibold mb-4">
                    {t('create_reseller')}
                </h2>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    data-tour="reseller-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="reseller-customer">
                            <FormItem
                                control={control}
                                name="customer_id"
                                label={t('customer_id')}
                                type="number"
                            />
                        </div>

                        <div data-tour="reseller-name">
                            <FormItem
                                control={control}
                                name="reseller_name"
                                label={t('reseller_name')}
                                type="textfield"
                            />
                        </div>

                        <div data-tour="reseller-tier">
                            <FormItem
                                control={control}
                                name="reseller_tier"
                                label={t('tier')}
                                type="select"
                                options={getResellerTierOptions(t)}
                            />
                        </div>
                    </div>

                    <div
                        className="flex gap-2 justify-end mt-6"
                        data-tour="reseller-submit"
                    >
                        <Button
                            variant="outlined"
                            onClick={() => navigate(paths.resellersList)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </Paper>
        </div>
    )
}

export default New
